import { migrationSignalScore } from './orbital-cost';
import { BODY_DEFINITIONS, type PlanetEcologyProfile } from './body-registry';
import { AQUATIC_RECIPE } from './biomass';
import { BiomassCycle } from './biomass-cycle';
import {
  CELESTIAL_BODY_SPECS,
  STAR_COLLISION_RADIUS,
  STAR_INDEX,
} from './celestial-system';
import { EmissionKind, type EmissionQueue } from './emissions';
import { EntityKind, type PrimaryStore } from './primary-store';
import type { SeededRandom } from './random';
import type { ResourceField } from './resource-field';
import type { PlanetClimate } from './planet-climate';
import { SURFACE_CELL_COUNT, surfaceCellForAngle as cellForWorldAngle } from './surface-coordinates';
import {
  BODY_SPIN_OFFSETS,
  bodySeasonSignal,
  bodySpinAngle,
} from './celestial-environment';

export const VEGETATION_CELLS_PER_PLANET = SURFACE_CELL_COUNT;
export const VEGETATION_VALUE_COUNT =
  CELESTIAL_BODY_SPECS.length * VEGETATION_CELLS_PER_PLANET;
const VEGETATION_RENEWAL_RATE = 1 / 16;
const SUNLIGHT_COLONIZATION_RATE = 0.00001;
/** Conservative surface redistribution, independently calibratable from growth. */
export const BASE_VEGETATION_DIFFUSION_RATE = 0.035 * VEGETATION_RENEWAL_RATE;
export const VEGETATION_TRANSPORT_PROFILE = { diffusionRate: BASE_VEGETATION_DIFFUSION_RATE };

export type { PlanetEcologyProfile } from './body-registry';

export const PLANET_ECOLOGY_PROFILES: readonly PlanetEcologyProfile[] =
  BODY_DEFINITIONS.map((body, slot) => ({
    ...body.ecology,
    supportsAlgae: CELESTIAL_BODY_SPECS[slot]?.supportsAlgae ?? false,
  }));

/** Capability profiles are indexed by the unified celestial-body registry. */
export const BODY_ECOLOGY_PROFILES = PLANET_ECOLOGY_PROFILES;

export class PlanetVegetationField {
  public matter: BiomassCycle | undefined;
  public readonly biomass = new Float64Array(VEGETATION_VALUE_COUNT);
  public readonly grazingStress = new Float32Array(VEGETATION_VALUE_COUNT);
  public readonly carrionSignal = new Float32Array(CELESTIAL_BODY_SPECS.length);
  private readonly nextBiomass = new Float64Array(VEGETATION_VALUE_COUNT);
  private readonly cellCapacity = new Float32Array(VEGETATION_VALUE_COUNT);
  private readonly spinAngles = Float64Array.from(BODY_SPIN_OFFSETS);

  public constructor(random: SeededRandom, private readonly climate?: PlanetClimate) {
    this.seed(random);
  }

  public reset(random: SeededRandom): void {
    this.grazingStress.fill(0);
    this.carrionSignal.fill(0);
    this.spinAngles.set(BODY_SPIN_OFFSETS);
    this.seed(random);
  }

  public update(
    store: PrimaryStore,
    resources: ResourceField,
    orbitalTime: number,
    deltaSeconds: number,
    climate: PlanetClimate | undefined = this.climate,
  ): void {
    const safeDelta = Math.max(0, deltaSeconds);
    if (climate !== undefined && this.matter === undefined) new BiomassCycle(store, this, resources, climate);
    if (this.matter !== undefined) this.matter.time = orbitalTime;
    for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
      const planet = CELESTIAL_BODY_SPECS[slot];
      const profile = PLANET_ECOLOGY_PROFILES[slot];
      if (planet === undefined || profile === undefined) {
        continue;
      }
      this.carrionSignal[slot] =
        (this.carrionSignal[slot] ?? 0) * Math.exp(-safeDelta / 32);
      if ((climate === undefined && !profile.supportsAlgae) ||
        (store.kind[planet.index] !== EntityKind.CelestialBody &&
          store.kind[planet.index] !== EntityKind.Moon)) {
        for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
          const index = vegetationIndex(slot, cell);
          this.matter?.recycleAlgae(slot, cell, this.biomass[index] ?? 0);
          this.biomass[index] = 0;
          this.nextBiomass[index] = 0;
          this.grazingStress[index] = 0;
        }
        continue;
      }
      const planetX = store.x[planet.index] ?? 0;
      const planetY = store.y[planet.index] ?? 0;
      const starX = store.x[STAR_INDEX] ?? 0;
      const starY = store.y[STAR_INDEX] ?? 0;
      const starAngle = Math.atan2(
        (store.y[STAR_INDEX] ?? 0) - planetY,
        (store.x[STAR_INDEX] ?? 0) - planetX,
      );
      const orbitAngle = Math.atan2(planetY - starY, planetX - starX);
      const spin = bodySpinAngle(slot, orbitalTime);
      this.spinAngles[slot] = spin;
      const season =
        1 + bodySeasonSignal(slot, orbitAngle) * profile.seasonalAmplitude;
      for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
        const index = vegetationIndex(slot, cell);
        const localAngle =
          ((cell + 0.5) / VEGETATION_CELLS_PER_PLANET) * Math.PI * 2;
        const worldAngle = localAngle + spin;
        const surfaceX =
          planetX + Math.cos(worldAngle) * (planet.bodyRadius + 0.008);
        const surfaceY =
          planetY + Math.sin(worldAngle) * (planet.bodyRadius + 0.008);
        const directDaylight = Math.max(
          0,
          Math.cos(worldAngle - starAngle),
        );
        const daylight =
          directDaylight *
          stellarVisibilityAt(store, surfaceX, surfaceY, planet.index);
        const nutrients = resources.sampleBody(slot, cell);
        const capacity =
          profile.carryingCapacity *
          (0.88 + Math.min(1, nutrients) * 0.12);
        this.cellCapacity[index] = capacity;
        const current = this.biomass[index] ?? 0;
        const grazingStress = this.grazingStress[index] ?? 0;
        const climateGrowth = climate?.growthFactor(slot) ?? 1;
        const photosyntheticDrive = daylight * 0.4 * climateGrowth;
        const establishedGrowth =
          Math.max(0.1, profile.productivity) *
          VEGETATION_RENEWAL_RATE *
          season *
          photosyntheticDrive *
          current *
          Math.max(0, 1 - current / Math.max(0.02, capacity)) *
          (1 - grazingStress * 0.92);
        // Incoming light can establish a tiny new colony after complete local
        // overgrazing. Darkness and a total eclipse provide no such source.
        const colonization =
          capacity *
          Math.max(0.1, profile.productivity) *
          season *
          daylight *
          SUNLIGHT_COLONIZATION_RATE *
          climateGrowth *
          (1 - grazingStress * 0.92);
        const requestedGrowth = establishedGrowth + colonization;
        const requestedAmount = Math.min(Math.max(0, capacity - current), requestedGrowth * safeDelta);
        const growthAmount = this.matter !== undefined
          ? this.matter.growAlgae(slot, cell, requestedAmount) : requestedAmount;
        const respiration =
          current *
          VEGETATION_RENEWAL_RATE *
          (0.002 + (1 - daylight) * 0.002);
        const respired = Math.min(current + growthAmount, respiration * safeDelta);
        const overflow = Math.max(0, current + growthAmount - respired - capacity);
        this.matter?.recycleAlgae(slot, cell, respired + overflow);
        const next = Math.max(0, current + growthAmount - respired - overflow);
        this.nextBiomass[index] = next;
        this.grazingStress[index] = Math.max(
          0,
          grazingStress - safeDelta * 0.025 * VEGETATION_RENEWAL_RATE -
            2 * growthAmount / Math.max(1e-8, capacity),
        );
        if (growthAmount > 0 && this.matter === undefined) {
          resources.consumeBody(
            slot,
            cell,
            growthAmount *
              0.014 /
              profile.nutrientEfficiency,
          );
        }
      }
    }
    // A single shared flux per edge conserves matter even when neighboring
    // cells have different grazing damage. Bound transfers by donor and room.
    for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
      for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
        const a = vegetationIndex(slot, cell);
        const b = vegetationIndex(slot, (cell + 1) % VEGETATION_CELLS_PER_PLANET);
        const difference = (this.nextBiomass[a] ?? 0) - (this.nextBiomass[b] ?? 0);
        const donor = difference >= 0 ? a : b;
        const receiver = difference >= 0 ? b : a;
        const conductance = 1 - Math.max(this.grazingStress[a] ?? 0, this.grazingStress[b] ?? 0) * 0.82;
        const amount = Math.min(Math.abs(difference) * 0.5 * VEGETATION_TRANSPORT_PROFILE.diffusionRate * conductance * safeDelta,
          this.nextBiomass[donor] ?? 0,
          Math.max(0, (this.cellCapacity[receiver] ?? 0) - (this.nextBiomass[receiver] ?? 0)));
        this.nextBiomass[donor] = (this.nextBiomass[donor] ?? 0) - amount;
        this.nextBiomass[receiver] = (this.nextBiomass[receiver] ?? 0) + amount;
      }
    }
    this.biomass.set(this.nextBiomass);
  }

  public forageGoal(
    store: PrimaryStore,
    planetSlot: number,
    x: number,
    y: number,
    output: Float64Array,
  ): number {
    const planet = CELESTIAL_BODY_SPECS[planetSlot];
    if (planet === undefined ||
      (store.kind[planet.index] !== EntityKind.CelestialBody &&
        store.kind[planet.index] !== EntityKind.Moon)) {
      output[0] = x;
      output[1] = y;
      return 0;
    }
    const centerX = store.x[planet.index] ?? 0;
    const centerY = store.y[planet.index] ?? 0;
    const radius = Math.max(
      planet.bodyRadius + 0.028,
      Math.min(
        planet.bodyRadius + 0.065,
        Math.hypot(x - centerX, y - centerY),
      ),
    );
    const spin = this.spinAngles[planetSlot] ?? 0;
    const currentCell = cellForWorldAngle(
      Math.atan2(y - centerY, x - centerX),
      spin,
    );
    let bestCell = currentCell;
    let bestScore = -1;
    for (let offset = -5; offset <= 5; offset += 1) {
      const cell =
        (currentCell + offset + VEGETATION_CELLS_PER_PLANET) %
        VEGETATION_CELLS_PER_PLANET;
      const biomass = this.biomass[vegetationIndex(planetSlot, cell)] ?? 0;
      const score = biomass - Math.abs(offset) * 0.018;
      if (score > bestScore) {
        bestScore = score;
        bestCell = cell;
      }
    }
    const worldAngle =
      ((bestCell + 0.5) / VEGETATION_CELLS_PER_PLANET) *
        Math.PI *
        2 +
      spin;
    output[0] = centerX + Math.cos(worldAngle) * radius;
    output[1] = centerY + Math.sin(worldAngle) * radius;
    return this.biomass[vegetationIndex(planetSlot, bestCell)] ?? 0;
  }

  public graze(
    store: PrimaryStore,
    herbivore: number,
    planetSlot: number,
    deltaSeconds: number,
    resources: ResourceField,
    emissions: EmissionQueue,
  ): number {
    const planet = CELESTIAL_BODY_SPECS[planetSlot];
    if (planet === undefined ||
      (store.kind[planet.index] !== EntityKind.CelestialBody &&
        store.kind[planet.index] !== EntityKind.Moon)) {
      return 0;
    }
    const x = store.x[herbivore] ?? 0;
    const y = store.y[herbivore] ?? 0;
    const centerX = store.x[planet.index] ?? 0;
    const centerY = store.y[planet.index] ?? 0;
    const surfaceDistance = Math.hypot(x - centerX, y - centerY);
    if (
      surfaceDistance < planet.bodyRadius ||
      surfaceDistance > planet.bodyRadius + 0.09
    ) {
      return 0;
    }
    const cell = cellForWorldAngle(
      Math.atan2(y - centerY, x - centerX),
      this.spinAngles[planetSlot] ?? 0,
    );
    const index = vegetationIndex(planetSlot, cell);
    const available = this.biomass[index] ?? 0;
    const energy = store.energy[herbivore] ?? 0;
    const hunger = Math.max(0, Math.min(1, (1.5 - energy) / 0.9));
    const feeding = Math.min(
      available,
      Math.max(0, deltaSeconds) * 0.026 * (0.12 + hunger * 0.88),
    );
    if (feeding <= 0) {
      return 0;
    }
    const removedBiomass = feeding;
    this.biomass[index] = available - removedBiomass;
    this.grazingStress[index] = Math.min(
      1,
      (this.grazingStress[index] ?? 0) + removedBiomass /
        Math.max(1e-8, this.cellCapacity[index] || PLANET_ECOLOGY_PROFILES[planetSlot]!.carryingCapacity),
    );
    store.energy[herbivore] =
      (store.energy[herbivore] ?? 0) + feeding * 1.8;
    const desiredGain = Math.max(0, Math.min(1.8 - (store.mass[herbivore] ?? 0), feeding * 0.08));
    if (store.matter !== undefined) {
      store.matter.assimilate(herbivore, AQUATIC_RECIPE, feeding, desiredGain);
    } else {
      store.mass[herbivore] = (store.mass[herbivore] ?? 0) + desiredGain;
      resources.depositBody(planetSlot, cell, removedBiomass * 0.12);
    }
    emissions.push(EmissionKind.Grazing, x, y, removedBiomass);
    return feeding;
  }

  public recycleMatter(
    store: PrimaryStore,
    resources: ResourceField,
    x: number,
    y: number,
    amount: number,
  ): void {
    const bodySlot = closestPlanetSlot(store, x, y);
    const body = CELESTIAL_BODY_SPECS[bodySlot];
    if (body === undefined ||
      (store.kind[body.index] !== EntityKind.CelestialBody &&
        store.kind[body.index] !== EntityKind.Moon)) {
      return;
    }
    const dx = x - (store.x[body.index] ?? 0);
    const dy = y - (store.y[body.index] ?? 0);
    if (Math.hypot(dx, dy) > body.bodyRadius + 0.18) {
      return;
    }
    const cell = cellForWorldAngle(
      Math.atan2(dy, dx),
      this.spinAngles[bodySlot] ?? 0,
    );
    resources.depositBody(bodySlot, cell, amount);
  }

  public normalizedSnapshot(): Float32Array {
    const output = new Float32Array(VEGETATION_VALUE_COUNT);
    for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
      const capacity =
        PLANET_ECOLOGY_PROFILES[slot]?.carryingCapacity ?? 1;
      for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
        const index = vegetationIndex(slot, cell);
        output[index] = Math.max(
          0,
          Math.min(1, (this.biomass[index] ?? 0) / capacity),
        );
      }
    }
    return output;
  }

  public coverage(): readonly number[] {
    return CELESTIAL_BODY_SPECS.map((_, slot) => this.meanForPlanet(slot));
  }

  public strongestGrazingSignalPlanet(excluding = -1, store?: PrimaryStore, consumer?: number): number {
    let strongestSlot = -1;
    let strongestSignal = 0.001;
    for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
      if (slot === excluding) {
        continue;
      }
      let signal = 0;
      for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
        signal += this.grazingStress[vegetationIndex(slot, cell)] ?? 0;
      }
      if (store !== undefined && consumer !== undefined) signal = migrationSignalScore(store, consumer, slot, signal);
      if (signal > strongestSignal) {
        strongestSignal = signal;
        strongestSlot = slot;
      }
    }
    return strongestSlot;
  }

  public recordCarrionSignal(
    store: PrimaryStore,
    x: number,
    y: number,
    amount = 1,
  ): void {
    const slot = closestPlanetSlot(store, x, y);
    this.carrionSignal[slot] = Math.min(
      8,
      (this.carrionSignal[slot] ?? 0) + Math.max(0, amount),
    );
  }

  public strongestCarrionSignalPlanet(excluding = -1, store?: PrimaryStore, consumer?: number): number {
    let strongestSlot = -1;
    let strongestSignal = 0.08;
    for (let slot = 0; slot < this.carrionSignal.length; slot += 1) {
      if (slot === excluding) {
        continue;
      }
      const rawSignal = this.carrionSignal[slot] ?? 0;
      const signal = store !== undefined && consumer !== undefined
        ? migrationSignalScore(store, consumer, slot, rawSignal) : rawSignal;
      if (signal > strongestSignal) {
        strongestSignal = signal;
        strongestSlot = slot;
      }
    }
    return strongestSlot;
  }

  public seedFromComet(planetSlot: number, firstCell: number): void {
    const profile = PLANET_ECOLOGY_PROFILES[planetSlot];
    if (profile === undefined || (this.climate !== undefined
      ? this.climate.growthFactor(planetSlot) <= 0.02 : !profile.supportsAlgae)) {
      return;
    }
    const first =
      ((firstCell % VEGETATION_CELLS_PER_PLANET) +
        VEGETATION_CELLS_PER_PLANET) %
      VEGETATION_CELLS_PER_PLANET;
    const second = (first + 11) % VEGETATION_CELLS_PER_PLANET;
    const previous = (this.biomass[vegetationIndex(planetSlot, first)] ?? 0) +
      (this.biomass[vegetationIndex(planetSlot, second)] ?? 0);
    this.biomass[vegetationIndex(planetSlot, first)] = Math.max(
      this.biomass[vegetationIndex(planetSlot, first)] ?? 0,
      profile.carryingCapacity * 0.16,
    );
    this.biomass[vegetationIndex(planetSlot, second)] = Math.max(
      this.biomass[vegetationIndex(planetSlot, second)] ?? 0,
      profile.carryingCapacity * 0.16,
    );
    this.grazingStress[vegetationIndex(planetSlot, first)] = 0;
    this.grazingStress[vegetationIndex(planetSlot, second)] = 0;
    this.matter?.importBiomass(AQUATIC_RECIPE,
      this.biomass[vegetationIndex(planetSlot, first)]! + this.biomass[vegetationIndex(planetSlot, second)]! - previous);
  }

  private meanForPlanet(slot: number): number {
    const capacity =
      PLANET_ECOLOGY_PROFILES[slot]?.carryingCapacity ?? 1;
    let sum = 0;
    for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
      sum += this.biomass[vegetationIndex(slot, cell)] ?? 0;
    }
    return Math.max(
      0,
      Math.min(1, sum / (VEGETATION_CELLS_PER_PLANET * capacity)),
    );
  }

  private seed(random: SeededRandom): void {
    for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
      const capacity =
        PLANET_ECOLOGY_PROFILES[slot]?.carryingCapacity ?? 1;
      if (this.climate !== undefined ? this.climate.growthFactor(slot) <= 0.02 : !PLANET_ECOLOGY_PROFILES[slot]?.supportsAlgae) {
        for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
          this.biomass[vegetationIndex(slot, cell)] = 0;
        }
        continue;
      }
      let seededTotal = 0;
      for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
        const wave =
          0.055 *
          Math.sin((cell / VEGETATION_CELLS_PER_PLANET) * Math.PI * 6 + slot);
        const index = vegetationIndex(slot, cell);
        const seeded = Math.max(
          0.1,
          0.3 + wave + random.normal() * 0.024,
        );
        this.biomass[index] = capacity * seeded;
        seededTotal += seeded;
      }
      const scale =
        (0.3 * VEGETATION_CELLS_PER_PLANET) /
        Math.max(0.001, seededTotal);
      for (let cell = 0; cell < VEGETATION_CELLS_PER_PLANET; cell += 1) {
        const index = vegetationIndex(slot, cell);
        this.biomass[index] = (this.biomass[index] ?? 0) * scale;
      }
    }
    this.nextBiomass.set(this.biomass);
  }
}

export function closestPlanetSlot(
  store: PrimaryStore,
  x: number,
  y: number,
): number {
  let closest = 0;
  let strongestField = 0;
  for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
    const planet = CELESTIAL_BODY_SPECS[slot];
    if (planet === undefined ||
      (store.kind[planet.index] !== EntityKind.CelestialBody &&
        store.kind[planet.index] !== EntityKind.Moon)) {
      continue;
    }
    const dx = x - (store.x[planet.index] ?? 0);
    const dy = y - (store.y[planet.index] ?? 0);
    const distanceSquared = dx * dx + dy * dy;
    const fieldStrength =
      planet.mass / Math.max(planet.bodyRadius ** 2, distanceSquared);
    if (fieldStrength > strongestField) {
      strongestField = fieldStrength;
      closest = slot;
    }
  }
  return closest;
}

export function ecologyProfileAt(
  store: PrimaryStore,
  x: number,
  y: number,
): PlanetEcologyProfile {
  return (
    PLANET_ECOLOGY_PROFILES[closestPlanetSlot(store, x, y)] ??
    PLANET_ECOLOGY_PROFILES[0]!
  );
}

function vegetationIndex(slot: number, cell: number): number {
  return slot * VEGETATION_CELLS_PER_PLANET + cell;
}

export function stellarVisibilityAt(
  store: PrimaryStore,
  targetX: number,
  targetY: number,
  excludedBodyIndex = -1,
): number {
  const starX = store.x[STAR_INDEX] ?? 0;
  const starY = store.y[STAR_INDEX] ?? 0;
  const rayX = targetX - starX;
  const rayY = targetY - starY;
  const targetDistance = Math.hypot(rayX, rayY);
  if (targetDistance <= STAR_COLLISION_RADIUS) {
    return 1;
  }
  const directionX = rayX / targetDistance;
  const directionY = rayY / targetDistance;
  let visibility = 1;
  for (const body of CELESTIAL_BODY_SPECS) {
    if (body.index === excludedBodyIndex || body.index >= store.count ||
      (store.kind[body.index] !== EntityKind.CelestialBody &&
        store.kind[body.index] !== EntityKind.Moon)) {
      continue;
    }
    const relativeX = (store.x[body.index] ?? 0) - starX;
    const relativeY = (store.y[body.index] ?? 0) - starY;
    const alongRay = relativeX * directionX + relativeY * directionY;
    if (alongRay <= STAR_COLLISION_RADIUS || alongRay >= targetDistance) {
      continue;
    }
    const toOccluderX = (store.x[body.index] ?? 0) - targetX;
    const toOccluderY = (store.y[body.index] ?? 0) - targetY;
    const distance = Math.max(0.0001, Math.hypot(toOccluderX, toOccluderY));
    const separation = Math.acos(Math.max(-1, Math.min(1,
      -(directionX * toOccluderX + directionY * toOccluderY) / distance,
    )));
    const coverage = circleOverlapFraction(STAR_COLLISION_RADIUS / targetDistance,
      body.bodyRadius / distance, separation);
    visibility = Math.min(visibility, 1 - coverage * 0.98);
  }
  return visibility;
}

/** Same finite-star disk overlap and numerical guards as the GLSL shader. */
export function circleOverlapFraction(source: number, occluder: number, separation: number): number {
  if (separation >= source + occluder) return 0;
  if (separation <= Math.abs(source - occluder)) {
    return occluder >= source ? 1 : occluder ** 2 / Math.max(1e-8, source ** 2);
  }
  const distance = Math.max(1e-8, separation);
  const projection = (distance ** 2 + source ** 2 - occluder ** 2) / (2 * distance);
  const opposite = distance - projection;
  const chord = Math.sqrt(Math.max(0, source ** 2 - projection ** 2));
  const area = source ** 2 * Math.acos(Math.max(-1, Math.min(1, projection / source))) +
    occluder ** 2 * Math.acos(Math.max(-1, Math.min(1, opposite / occluder))) - distance * chord;
  return Math.max(0, Math.min(1, area / Math.max(1e-8, Math.PI * source ** 2)));
}
